<i18n>
{
  "no": {
    "youNeedToContinueWithTelenorID": "Fortsett med Telenor ID",
    "phoneNumber": "Mobilnummer",
    "phoneNumberPlaceholder": "Skriv inn ditt mobilnummer",
    "infoMessage": "Nummeret hjelper oss å bekrefte identiteten din",
    "badInput": "Ugyldig mobilnummer. Husk landkode hvis nummeret ikke er norsk.",
    "next": "Fortsett med Telenor ID",
    "genericError": "Noe gikk galt, prøv igjen senere.",
    "maintenance": {
      "header": "Vedlikehold",
      "text": "Innlogging vil være utilgjengelig mens vi jobber med vedlikehold på våre digitale tjenester. Vi beklager eventuelle ulemper dette måtte medføre"
    }
  },
  "en": {
    "youNeedToContinueWithTelenorID": "Continue with Telenor ID",
    "phoneNumber": "Mobile number",
    "phoneNumberPlaceholder": "Enter your mobile number",
    "infoMessage": "Your number helps us verify your identity",
    "badInput": "Invalid mobile number. Remember country code if the number is not Norwegian.",
    "next": "Continue with Telenor ID",
    "genericError": "Something went wrong. Wait a few moments and try again",
    "maintenance": {
      "header": "Maintenance",
      "text": "Login will be unavailable while we work on maintenance of our digital services. We apologize for any inconvenience this may cause"
    }
  }
}
</i18n>

<template>
  <div v-if="!isMaintenance" class="page">
    <FormWrapper>
      <div>
        <TnHeading type="h1" size="s" align="center" class="margin-vertical-2xl">
          {{ t('youNeedToContinueWithTelenorID') }}
        </TnHeading>

        <TnNotificationInline
          v-if="requestError"
          horizontal
          status="critical"
          full-width
          class="margin-bottom-l"
        >
          {{ requestError }}
        </TnNotificationInline>

        <form class="narrow-form" @submit.prevent="submitForm">
          <TnTextField
            id="txtLoginHint"
            v-model="loginHint"
            name="login_hint"
            type="tel"
            inputmode="tel"
            autocomplete="tel-national"
            :label="t('phoneNumber')"
            :placeholder="t('phoneNumberPlaceholder')"
            :description="t('infoMessage')"
            :error="validationError"
            @blur="touched = true"
          />

          <TnButton id="btnGo" type="submit" primary block class="margin-top-l">
            <img :src="telenorLogoWhite" alt="" aria-hidden="true" class="btn-logo" />
            {{ t('next') }}
          </TnButton>
        </form>
      </div>
    </FormWrapper>
  </div>

  <div v-else class="page">
    <TnHeading type="h1" size="m" align="center" class="margin-vertical-2xl">
      {{ t('maintenance.header') }}
    </TnHeading>
    <TnNotificationInline horizontal status="warning" full-width>
      {{ t('maintenance.text') }}
    </TnNotificationInline>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { isValidPhoneNumber } from 'libphonenumber-js';
import {
  TnButton,
  TnHeading,
  TnNotificationInline,
  TnTextField,
} from '@telenornorgeinternal/telegrafen-vue3-core';

import FormWrapper from '@/components/FormWrapper.vue';
import telenorLogoWhite from '@/assets/telenor-logo-flat-white.svg';
import { RequestParsingService } from '@/views/ibis/requestParsing.service';
import { ErrorService } from '@/error/error.service';
import { EnvironmentService } from '@/environment';

const { t } = useI18n();
const route = useRoute();

const environment = ref<Record<string, string>>({});
const loginHint = ref<string>((route.query.login_hint as string) ?? '');
const touched = ref(false);

const isMaintenance = computed(() => environment.value.maintenance === 'true');

function validateNorwegianDataNumber(value: string) {
  return /^\d{12}$/.test(value) && value.startsWith('58');
}

const isValid = computed(
  () =>
    isValidPhoneNumber(loginHint.value ?? '', 'NO') ||
    validateNorwegianDataNumber(loginHint.value ?? ''),
);

// NOTE: must be `undefined` (not `''`) when valid — TnTextField declares `error` as
// [Boolean, String], so Vue's boolean casting turns an empty string into `true`.
const validationError = computed(() =>
  touched.value && !isValid.value ? t('badInput') : undefined,
);

const requestError = computed(() => {
  const request = RequestParsingService.parseUrlEncodedJson(route.query.requestUrlEncoded);
  const errorCode = (request as { errorCode?: number } | null)?.errorCode;

  if (!errorCode) return '';

  console.debug(`IBIS error code: ${errorCode}`);
  const error = ErrorService.getError('ibis', errorCode);

  return error.errorCode === 40002 ? t('badInput') : t(error.i18nPath);
});

const authorizeLink = computed(() => `${import.meta.env.VITE_KOOKABURRA_BASE_URL}/oauth/authorize`);

const queryParams = computed(() => {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries({ ...route.query, login_hint: loginHint.value })) {
    if (value != null) params.append(key, String(value));
  }

  return params.toString();
});

function submitForm() {
  window.location.href = `${authorizeLink.value}?${queryParams.value}`;
}

onMounted(async () => {
  environment.value = await EnvironmentService.getEnvironment();
});
</script>

<style scoped>
.narrow-form {
  max-width: 430px;
  margin-inline: auto;
}
</style>
